/**
 * The arithmetic behind a stat tile's count-up (components/magic/count-up).
 * A tile's value arrives already formatted ("₹1,23,456", "12.4%", "1,450",
 * "3.2x"), so the tween animates the one number inside the string and keeps
 * everything around it. The last frame always writes the original string
 * itself, so the number a reader is left with is exactly the one computed.
 *
 * Countable means: exactly one run of digits (with its own grouping commas
 * and decimals), no other digit anywhere, and a value above zero. "2 of 5",
 * "14:05", "2026-10-31" and "-" are left as they are.
 */
const NUM = /\d[\d,]*(?:\.\d+)?/g;

export function parseCountable(text) {
  if (typeof text !== 'string' || text.length > 32) return null;
  const runs = text.match(NUM);
  if (!runs || runs.length !== 1) return null;
  const token = runs[0];
  const at = text.indexOf(token);
  const prefix = text.slice(0, at);
  const suffix = text.slice(at + token.length);
  const [intPart, frac = ''] = token.split('.');
  const groups = intPart.split(',');
  if (groups.some((g) => g === '')) return null;
  const digits = groups.join('');
  if (digits.length > 1 && digits[0] === '0') return null; // 007, 05: an id, not an amount
  let group = 'none';
  if (groups.length > 1) {
    const [first, ...rest] = groups;
    const last = rest[rest.length - 1];
    const middle = rest.slice(0, -1);
    if (first.length < 1 || first.length > 3 || last.length !== 3) return null;
    if (middle.every((g) => g.length === 3)) group = 'western';
    else if (middle.every((g) => g.length === 2) && first.length <= 2) group = 'indian';
    else return null;
  }
  const target = Number(`${digits}${frac ? `.${frac}` : ''}`);
  if (!Number.isFinite(target) || target <= 0) return null;
  return { prefix, suffix, target, decimals: frac.length, group, text };
}

function groupDigits(intStr, group) {
  if (group === 'none' || intStr.length <= 3) return intStr;
  if (group === 'western') return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  // Indian: the last three, then pairs (1,23,45,678).
  const head = intStr.slice(0, -3);
  const tail = intStr.slice(-3);
  return `${head.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${tail}`;
}

/** The string for an in-between value n (0..target), in the original's own shape. */
export function formatCountable(parsed, n) {
  if (n >= parsed.target) return parsed.text;
  const fixed = Math.max(0, n).toFixed(parsed.decimals);
  const [i, f] = fixed.split('.');
  return `${parsed.prefix}${groupDigits(i, parsed.group)}${f ? `.${f}` : ''}${parsed.suffix}`;
}
